/**
 * Label component — shared types and Zod prop schema.
 *
 * A small pill/badge element for callouts and annotations.
 * Supports three content modes: text only, icon + text, icon only.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { IconNameEnum } from '../Icon/Icon';

// `l` is icon-only-only (like `m`) — it exists solely to match ListitemCard's
// native leading-media chip geometry (see LABEL_ICON_ONLY_LARGE_SIZE below),
// so an icon-only Label can sit next to a ListitemCard's leading icon and read
// as the same pill treatment instead of a visibly smaller, differently-rounded one.
//
// This mirrors the geometry via shared canonical tokens + a drift-guard test
// (Label.test.ts asserts against ListitemCard's own exported constants)
// rather than importing ListitemCard's internal `Media()` renderer directly:
// `Media()` isn't exported (it's a private helper) and its accessibility
// props hardcode "always a decorative icon inside a ListitemCard"
// (`accessible={false}`, `importantForAccessibility="no-hide-descendants"`)
// — assumptions that don't hold for a standalone catalog element like this
// one, which needs its own `accessibilityLabel`. Same "one canonical value,
// two call sites, test enforces they can't drift" shape as ADR-066's
// rowResponse drift-guard pattern.
export const LabelSize = z.enum(['sm', 'm', 'l']);
export type LabelSize = z.infer<typeof LabelSize>;

// Surface-relationship descriptor (ADR-020 Rule 4): `dark` inverts the default
// foreground for placement on a dark surface. Explicit colour props still win.
export const LabelTone = z.enum(['light', 'dark']);
export type LabelTone = z.infer<typeof LabelTone>;

/**
 * Outline of the text container. `rectangle` is the pill/badge shape; `circle`
 * renders a fixed-diameter disc (the `width`, or the default control diameter)
 * with the content centred — a numbered step marker, for example.
 */
export const LabelShape = z.enum(['rectangle', 'circle']);
export type LabelShape = z.infer<typeof LabelShape>;

export const labelPropsSchema = z.object({
  label: z.string().optional(),
  iconName: IconNameEnum.optional(),
  icon: z.boolean().default(false),
  size: LabelSize.default('sm'),
  tone: LabelTone.default('light'),
  backgroundColor: z.string().optional(),
  textColor: z.string().optional(),
  // A fixed pixel width for a caller that must match another element's
  // geometry exactly (e.g. a column header spanning a sibling row's fixed-width
  // section). Overrides the default hug-content sizing; content centers within it.
  width: z.number().positive().optional(),
  shape: LabelShape.default('rectangle'),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

// Input shape so defaulted fields (`icon`, `size`, `tone`) stay optional for callers.
export type LabelProps = z.input<typeof labelPropsSchema>;

/** Default colours from the Figma design system. */
export const LABEL_DEFAULT_BACKGROUND = colors.background.callout;
export const LABEL_DEFAULT_TEXT_COLOR = colors.text.primary;
export const LABEL_DEFAULT_ICON_COLOR = colors.icon.primary;

/** Typography style for label text. */
export const LABEL_TYPOGRAPHY = typography.c1Regular;

/** Border radius for the `sm` / `m` sizes — always the small (4px) token. */
export const LABEL_RADIUS = radius.sm;

/** Border radius for the `circle` shape — the fully rounded token. */
export const LABEL_CIRCLE_RADIUS = radius.rounded;
/** Diameter of a `circle` label when no `width` is given — the default control size. */
export const LABEL_CIRCLE_DEFAULT_DIAMETER = sizing.controlButton.md;

/** Resolve the disc diameter for the `circle` shape. */
export function labelCircleDiameterFor(width?: number): number {
  return width ?? LABEL_CIRCLE_DEFAULT_DIAMETER;
}

/** Icon size per label size. */
export const iconSizeForLabelSize: Record<LabelSize, number> = {
  sm: 12,
  m: 20,
  l: spacing.lg, // 24 — matches ListitemCard's native media-chip icon size
};

/** Horizontal padding for sm variants. */
export const LABEL_HORIZONTAL_PADDING = spacing.s;

/** Vertical (top/bottom) padding for text variants. */
export const LABEL_VERTICAL_PADDING = spacing.xs;

/** Gap between icon and text for icon+text variant. */
export const LABEL_ICON_TEXT_GAP = spacing.xs;

/** Internal gap for text-only variant (not token-bound in Figma). */
export const LABEL_TEXT_ONLY_GAP = 6;

/** Fixed dimensions for the icon-only (size=m) variant. */
export const LABEL_ICON_ONLY_SIZE = 32;

/** Fixed dimensions + radius for the icon-only (size=l) variant — identical to
 * ListitemCard's native leading-media chip (spacing.xxl / radius.m). */
export const LABEL_ICON_ONLY_LARGE_SIZE = spacing.xxl;
export const LABEL_ICON_ONLY_LARGE_RADIUS = radius.m;

/**
 * Resolve the effective colours for a Label instance.
 *
 * Precedence: an explicit `textColor` always wins (and the icon fill inherits it
 * so text and icon read as one unit). Otherwise `tone` picks the default — `dark`
 * inverts the foreground for a dark surface, `light` uses the primary defaults.
 * `backgroundColor` is independent of tone.
 */
export function labelColorsFor(
  backgroundColor?: string,
  textColor?: string,
  tone: LabelTone = 'light',
): { background: string; text: string; icon: string } {
  const toneText = tone === 'dark' ? colors.text.inverted : LABEL_DEFAULT_TEXT_COLOR;
  const toneIcon = tone === 'dark' ? colors.icon.inverted : LABEL_DEFAULT_ICON_COLOR;
  return {
    background: backgroundColor ?? LABEL_DEFAULT_BACKGROUND,
    text: textColor ?? toneText,
    icon: textColor ?? toneIcon,
  };
}
